<template>
  <!-- content-wrapper/content, not a bare .report-page: DefaultLayout renders a
       FIXED navbar and sidebar and then the router-view with no offset of its
       own, so a route component that omits .content-wrapper (src/theme/Base.scss)
       paints from 0,0 — under the navbar and behind the sidebar. The tier reports
       do not hit this because they are mounted inside TierReport.vue, which
       carries the wrapper; this report IS the route component. -->
  <div class="content-wrapper view-voucher-batch-report p-0">
    <div class="content">
      <div class="report-page">
        <div class="card">
          <div class="card-body">
            <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

            <div v-if="isLoading && !batch" class="text-center text-muted py-4">
              <b-spinner small class="mr-2"></b-spinner>กำลังโหลดรายงาน...
            </div>

            <div v-if="batch" class="batch-header d-flex align-items-center flex-wrap mb-3">
              <h4 class="mb-0 mr-2">{{ batch.name }}</h4>
              <b-badge v-if="isExpired" variant="danger" class="mr-2">หมดอายุแล้ว</b-badge>
              <span class="text-muted mr-3">คำนำหน้ารหัส {{ batch.codePrefix }}</span>
              <span class="text-muted">{{ validityLine }}</span>
            </div>

            <div v-if="summary" class="summary-tiles d-flex flex-wrap mb-3">
              <div class="summary-tile">
                <div class="summary-tile-label">ออก</div>
                <div class="summary-tile-value">{{ summary.issued }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ใช้แล้ว</div>
                <div class="summary-tile-value">{{ summary.redeemed }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">คงเหลือ</div>
                <div class="summary-tile-value">{{ summary.remaining }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">หมดอายุ</div>
                <div class="summary-tile-value">{{ summary.expired }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ยกเลิก</div>
                <div class="summary-tile-value">{{ summary.voided }}</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">อัตราการใช้</div>
                <div class="summary-tile-value">{{ summary.rate }}%</div>
              </div>
              <div class="summary-tile">
                <div class="summary-tile-label">ยอดส่วนลดรวม</div>
                <div class="summary-tile-value">{{ summary.totalDiscount | comma }} บาท</div>
              </div>
            </div>

            <template v-if="batch">
              <h5 class="mb-2">ยอดส่วนลดแยกตามสาขา</h5>
              <table class="table table-sm table-bordered mb-4">
                <thead class="thead-light">
                  <tr>
                    <th>สาขา</th>
                    <th class="text-right">ยอดส่วนลด (บาท)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!byBranch.length">
                    <td colspan="2" class="text-center text-muted">ยังไม่มีการใช้บัตรในชุดนี้</td>
                  </tr>
                  <tr v-for="row in byBranch" v-else :key="row.branchId">
                    <td>{{ row.branchName }}</td>
                    <td class="text-right">{{ row.discount | comma }}</td>
                  </tr>
                </tbody>
              </table>

              <div class="toolbar d-flex align-items-center flex-wrap mb-2">
                <div class="field mr-3">
                  <label class="field-lbl">กรองตามสถานะ</label>
                  <b-form-select
                    v-model="statusFilter"
                    :options="statusOptions"
                    size="sm"
                    class="field-input-native"
                  ></b-form-select>
                </div>
                <div class="spacer flex-grow-1"></div>
                <b-dropdown text="ส่งออก" size="sm" variant="outline-success" right :disabled="!codes.length">
                  <b-dropdown-item @click="exportExcel">
                    <i class="fas fa-file-excel mr-2"></i>Excel
                  </b-dropdown-item>
                  <b-dropdown-item @click="exportPdf">
                    <i class="fas fa-file-pdf mr-2"></i>PDF
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <b-table
                id="voucherBatchReportTable"
                :items="codes"
                :fields="fields"
                :busy="isLoading"
                responsive
                hover
                show-empty
                empty-text="ไม่มีข้อมูลให้แสดง"
              >
                <template #cell(status)="{ item }">{{ statusLabel(item.status) }}</template>
                <template #cell(redeemedDt)="{ value }">{{ fmtDate(value) }}</template>
                <template #cell(assignedName)="{ item }">
                  <span v-if="!item.assignedName" class="text-muted">ใครก็ใช้ได้</span>
                  <span v-else>{{ item.assignedName }}<small v-if="item.assignedHn" class="text-muted ml-1">({{ item.assignedHn }})</small></span>
                </template>
                <template #cell(patientHn)="{ value }">{{ value || "—" }}</template>
                <template #cell(patientName)="{ value }">{{ value || "—" }}</template>
                <template #cell(branchName)="{ value }">{{ value || "—" }}</template>
                <template #cell(redeemedByName)="{ value }">{{ value || "—" }}</template>
                <template #cell(assignActions)="{ item }">
                  <!-- Only UNUSED rows: once a card is redeemed or voided its
                       holder is frozen history and the BE refuses to change it,
                       so offering the button would be offering a 404. -->
                  <b-button
                    v-if="canAssign && item.status === 'UNUSED'"
                    size="sm"
                    variant="outline-secondary"
                    @click="openAssign(item)"
                  >
                    <i class="fas fa-user-tag mr-1"></i>{{ item.assignedName ? "แก้ไขผู้ถือ" : "ผูกคนไข้" }}
                  </b-button>
                </template>
              </b-table>

              <b-modal
                ref="assignModal"
                title="ผู้ถือบัตรกำนัล"
                ok-title="บันทึก"
                cancel-title="ยกเลิก"
                :ok-disabled="isAssigning"
                @ok.prevent="submitAssign"
              >
                <b-alert :show="!!assignError" variant="danger">{{ assignError }}</b-alert>
                <p class="mb-2">
                  รหัสบัตร <strong>{{ assignTarget && assignTarget.code }}</strong>
                </p>
                <p class="text-muted small mb-3">
                  เมื่อผูกกับคนไข้แล้ว คนอื่นจะใช้บัตรใบนี้ไม่ได้ — ล้างการผูกเพื่อให้ใครก็ใช้ได้
                </p>

                <div v-if="assignPatient" class="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                  <span>
                    {{ assignPatient.firstNameTh }} {{ assignPatient.lastNameTh }}
                    <small class="text-muted ml-1">{{ assignPatient.dn }}</small>
                  </span>
                  <b-button size="sm" variant="outline-danger" @click="assignPatient = null">
                    ล้าง
                  </b-button>
                </div>

                <template v-if="!assignPatient">
                  <b-form-radio-group
                    v-model="assignScope"
                    size="sm"
                    class="mb-2"
                    @change="searchAssignPatients"
                  >
                    <b-form-radio value="BRANCH">เฉพาะสาขานี้</b-form-radio>
                    <b-form-radio value="CLINIC">ทุกสาขาในคลินิก</b-form-radio>
                  </b-form-radio-group>
                  <b-form-input
                    v-model="assignSearch"
                    placeholder="ค้นหาคนไข้ (ชื่อ หรือ HN)"
                    @input="searchAssignPatients"
                  ></b-form-input>
                </template>
                <b-list-group
                  v-if="!assignPatient && assignResults.length"
                  class="mt-2"
                  style="max-height: 200px; overflow-y: auto"
                >
                  <b-list-group-item
                    v-for="p in assignResults"
                    :key="p.id"
                    button
                    @click="assignPatient = p"
                  >
                    {{ p.firstNameTh }} {{ p.lastNameTh }}
                    <!-- HN, always shown: it is unique per patient and is the
                         only thing distinguishing two same-named people when
                         searching across every branch. getPatientList does not
                         return the patient's branch for JWT callers. -->
                    <small class="text-muted ml-1">{{ p.dn }}</small>
                  </b-list-group-item>
                </b-list-group>
              </b-modal>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import moment from "moment";
import { PERMISSIONS } from "@/constants/roles";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

export default {
  name: "VoucherBatchReport",
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      statusFilter: null,
      report: null,
      // ผู้ถือบัตร (who MAY use it) sits beside the code it governs, ahead of
      // the redemption columns (who DID use it). The two are different facts
      // and are deliberately not merged — on a bound card redeemed by its own
      // holder they show the same person, which is not duplication.
      fields: [
        { key: "code", label: "รหัสบัตร" },
        { key: "assignedName", label: "ผู้ถือบัตร" },
        { key: "status", label: "สถานะ" },
        { key: "redeemedDt", label: "วันที่ใช้" },
        { key: "patientHn", label: "HN" },
        { key: "patientName", label: "ชื่อคนไข้" },
        { key: "branchName", label: "สาขาที่ใช้" },
        { key: "redeemedByName", label: "พนักงานที่ตัดใช้" },
        // Excluded from the Excel/PDF export by reportExport's own column
        // handling only if it renders empty; it holds a button, so it is
        // appended last to keep it out of the middle of an exported table.
        { key: "assignActions", label: "" },
      ],
      assignTarget: null,
      assignPatient: null,
      assignSearch: "",
      assignResults: [],
      // Defaults to this branch: a card is valid clinic-wide, so a sibling
      // branch's patient is a legitimate holder, but most bindings are local
      // and a clinic-wide default would bury them in same-name matches.
      assignScope: "BRANCH",
      assignError: "",
      isAssigning: false,
      statusOptions: [
        { value: null, text: "ทุกสถานะ" },
        { value: "UNUSED", text: "ยังไม่ใช้" },
        { value: "REDEEMED", text: "ใช้แล้ว" },
        { value: "VOID", text: "ยกเลิก" },
      ],
      reportTitle: "รายงานบัตรกำนัล",
      reportNameEn: "VoucherBatch",
    };
  },
  computed: {
    ...mapGetters({
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
      can: "moduleUser/can",
    }),
    // voucher.assign, NOT voucher.manage: a branch MANAGER can re-point a card
    // but cannot mint a batch. Re-pointing has to be reachable from a branch —
    // it is the only remedy when the wrong holder presents a card, since
    // redemption hard-rejects with no counter override.
    canAssign() {
      return this.can(PERMISSIONS.VOUCHER_ASSIGN, {
        clinicId: this.branchInfo?.clinic?.id,
      });
    },
    batch() { return this.report?.batch || null; },
    codes() { return this.report?.codes || []; },
    // Expiry is derived from the batch, never stored per code — see the note above.
    isExpired() {
      if (!this.batch?.validTo) return false;
      return moment(this.batch.validTo).endOf("day").isBefore(moment());
    },
    summary() {
      const b = this.batch;
      if (!b) return null;
      const unused = b.remaining || 0;
      return {
        issued: b.issued || 0,
        redeemed: b.redeemed || 0,
        remaining: this.isExpired ? 0 : unused,
        expired: this.isExpired ? unused : 0,
        voided: b.voided || 0,
        rate: b.redemptionRate || 0,
        totalDiscount: b.totalDiscount || 0,
      };
    },
    byBranch() { return this.batch?.discountByBranch || []; },
    // "Never expires" is decided by validTo alone — validFrom and validTo are
    // independently optional on the batch, so all four combinations need a
    // distinct sentence (see fix-round-1 note).
    validityLine() {
      const b = this.batch;
      if (!b) return "";
      const { validFrom, validTo } = b;
      if (!validFrom && !validTo) return "ไม่มีวันหมดอายุ";
      if (validFrom && !validTo) return `เริ่ม ${this.fmtDate(validFrom)} · ไม่มีวันหมดอายุ`;
      if (!validFrom && validTo) return `ถึง ${this.fmtDate(validTo)}`;
      return `${this.fmtDate(validFrom)} – ${this.fmtDate(validTo)}`;
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["fetchBatchReport", "assignCode"]),

    openAssign(item) {
      this.assignTarget = item;
      this.assignError = "";
      this.assignSearch = "";
      this.assignResults = [];
      // Preselect the current holder so "แก้ไขผู้ถือ" starts from who has it,
      // and so saving without touching anything is a no-op rather than an
      // accidental unbind. assignedPatientId is what makes this possible — the
      // name alone could not be turned back into an id.
      this.assignPatient = item.assignedPatientId
        ? {
            id: item.assignedPatientId,
            dn: item.assignedHn,
            firstNameTh: item.assignedName,
            lastNameTh: "",
          }
        : null;
      this.$refs.assignModal.show();
    },

    // One endpoint, two scopes. getPatientList always scopes to the clinic the
    // caller's branch membership authorises; `branchId` narrows it further, and
    // OMITTING it searches every branch (patient_repo/getPatientList does
    // `if (branchId)`). So clinic-wide is the absence of the param, not a
    // separate route — and the BE accepts any patient in the clinic as a
    // holder, so both scopes yield bindable people.
    async searchAssignPatients() {
      if (this.assignSearch.length < 2) {
        this.assignResults = [];
        return;
      }
      try {
        const params = { keyword: this.assignSearch, limit: 20 };
        if (this.assignScope === "BRANCH" && this.branchInfo?.id) {
          params.branchId = this.branchInfo.id;
        }
        const res = await axios.get(
          `/api/patient/${this.$route.params.clinicUrl}/${this.$route.params.branchUrl}`,
          { params }
        );
        this.assignResults = res.data?.patients || res.data || [];
      } catch {
        this.assignResults = [];
      }
    },

    async submitAssign() {
      if (!this.assignTarget) return;
      this.isAssigning = true;
      this.assignError = "";
      try {
        await this.assignCode({
          clinicUrl: this.$route.params.clinicUrl,
          batchId: this.$route.params.batchId,
          codeId: this.assignTarget.id,
          // null unbinds — an explicit choice the operator made by clearing the
          // selection, not an accident of an empty field.
          patientId: this.assignPatient ? this.assignPatient.id : null,
        });
        this.$refs.assignModal.hide();
        await this.load();
      } catch (error) {
        this.assignError = error?.response?.data?.message || "บันทึกผู้ถือบัตรไม่สำเร็จ";
      } finally {
        this.isAssigning = false;
      }
    },


    statusLabel(s) {
      if (s === "UNUSED" && this.isExpired) return "หมดอายุ";
      return { UNUSED: "ยังไม่ใช้", REDEEMED: "ใช้แล้ว", VOID: "ยกเลิก" }[s] || s;
    },
    fmtDate(v) { return v ? moment(v).format("DD/MM/YYYY HH:mm") : "—"; },

    async load() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        this.report = await this.fetchBatchReport({
          clinicUrl: this.$route.params.clinicUrl,
          batchId: this.$route.params.batchId,
          status: this.statusFilter,
        });
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || "โหลดรายงานไม่สำเร็จ";
      } finally {
        this.isLoading = false;
      }
    },


    // The shared helper reads the rendered table out of the DOM by id and adds
    // the standard DentCloud report header. The batch's own validity range
    // stands in for the date range every other report passes.
    exportOptions() {
      return {
        tableId: "voucherBatchReportTable",
        reportTitle: `${this.reportTitle} — ${this.batch ? this.batch.name : ""}`.trim(),
        reportNameEn: this.reportNameEn,
        dateFrom: this.batch && this.batch.validFrom,
        dateTo: this.batch && this.batch.validTo,
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    exportExcel() {
      exportTableToExcel(this.exportOptions());
    },
    exportPdf() {
      exportReportToPdf(this.exportOptions());
    },
  },
  watch: {
    statusFilter() { this.load(); },
  },
  created() { this.load(); },
};
</script>

<style scoped>
.summary-tiles {
  gap: 10px;
}
.summary-tile {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 14px;
  min-width: 100px;
}
.summary-tile-label {
  font-size: 0.75rem;
  color: #6c757d;
}
.summary-tile-value {
  font-size: 1.15rem;
  font-weight: 700;
}
</style>
